import { Ban, CalendarPlus, Zap } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageBody, PageHeader } from '@/components/layout/page-header';
import { DataTable, TabelaPainel, type Coluna } from '@/components/patterns/data-table';
import { FilterBar, FilterBusca, FilterLimpar, FilterSelect } from '@/components/patterns/filter-bar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { StatusAgendamentoBadge } from '@/components/patterns/status-badge';
import { PermissionButton } from '@/components/patterns/permission-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { dataSource } from '@/data';
import type { Agendamento, Instalacao } from '@/data/types';
import { useAsyncAction } from '@/hooks/use-async-action';
import { useRealtimeAgendamentos } from '@/hooks/use-realtime-agendamentos';
import { useResource } from '@/hooks/use-resource';
import { formatarData, plural, texto } from '@/lib/format';
import { indexarInstalacoes, podeCancelar } from '@/lib/rules';

/**
 * Agendamentos programados.
 *
 * Hub de agendamentos ATIVOS — só Programado/Executando. Cancelar é uma
 * transição de estado no backend (POST .../transicoes/cancelar), não uma
 * exclusão (por isso o rótulo aqui é "Cancelar", nunca "Excluir"), mas a
 * linha SAI desta lista ao cancelar: o servidor só devolve os dois status
 * ativos, e um agendamento cancelado só continua visível no Histórico. A
 * confirmação é um Dialog que nomeia a instalação e o horário: um `confirm()`
 * genérico não dá ao usuário a chance de perceber que selecionou a linha errada.
 */
/** Só sysadmin — bypassa a espera do RedBeat e dispara o agendamento na
 * hora. Restrito de propósito (nem `sre` entra aqui, ao contrário do bypass
 * padrão do resto do app): é uma ação de produção fora do fluxo normal, não
 * uma ferramenta de suporte do dia a dia. O backend é quem garante de
 * verdade (403 pra qualquer outro papel) — isto aqui só esconde o botão. */
const PODE_FORCAR_DISPARO = ['sysadmin'];

export function AgendamentosPage({ roles = [] }: { roles?: string[] }) {
  const podeForcarDisparo = PODE_FORCAR_DISPARO.some((role) => roles.includes(role));
  const carregar = useCallback(async (signal: AbortSignal) => {
    const [agendamentos, instalacoes] = await Promise.all([
      dataSource.listAgendamentos(signal),
      dataSource.listInstalacoes(signal),
    ]);
    return { agendamentos, instalacoes };
  }, []);

  const recurso = useResource<{ agendamentos: Agendamento[]; instalacoes: Instalacao[] }>(
    carregar,
    [],
    // O WebSocket abaixo só avisa criação/cancelamento — quem transiciona
    // Programado → Executando → (sai do hub) é o worker, que não publica
    // nada nesse canal. Este poll (silencioso, sem piscar "carregando") é a
    // única coisa que pega essa mudança; 15s porque "Executando" costuma ser
    // uma janela curta e essa tela é usada pra acompanhar isso ao vivo.
    { intervaloAtualizacaoMs: 15_000 },
  );
  // Duas pessoas podem estar olhando o mesmo agendamento — sem isso, cancelar
  // algo que a outra já cancelou só se descobre ao tentar (o servidor barra,
  // mas a tela fica desatualizada até então).
  useRealtimeAgendamentos(recurso.atualizarEmSilencio);

  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [aCancelar, setACancelar] = useState<Agendamento | null>(null);
  const [aDisparar, setADisparar] = useState<Agendamento | null>(null);

  const cancelamento = useAsyncAction({
    sucesso: 'Agendamento cancelado.',
    contextoDoErro: 'Não foi possível cancelar o agendamento',
    aoConcluir: () => {
      setACancelar(null);
      recurso.recarregar();
    },
  });

  const disparoForcado = useAsyncAction({
    sucesso: 'Disparo forçado — deve iniciar em instantes.',
    contextoDoErro: 'Não foi possível forçar o disparo',
    aoConcluir: () => {
      setADisparar(null);
      recurso.recarregar();
    },
  });

  const agendamentos = useMemo(() => recurso.dados?.agendamentos ?? [], [recurso.dados]);
  const porInstalacao = useMemo(() => indexarInstalacoes(recurso.dados?.instalacoes ?? []), [recurso.dados]);

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return agendamentos.filter((item) => {
      if (status && item.status !== status) return false;
      if (!termo) return true;
      return `${item.instalacao} ${item.versao} ${item.batchId ?? ''}`.toLowerCase().includes(termo);
    });
  }, [agendamentos, busca, status]);

  const filtrosAtivos = [busca.trim() !== '', status !== null].filter(Boolean).length;

  const colunas: Array<Coluna<Agendamento>> = [
    {
      id: 'instalacao',
      cabecalho: 'Instalação',
      ordenarPor: (item) => item.instalacao,
      celula: (item) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-bold">{texto(item.instalacao)}</span>
          <span className="font-mono text-[11px] text-on-surface-muted">{texto(item.id)}</span>
        </div>
      ),
    },
    {
      id: 'quando',
      cabecalho: 'Data e horário',
      largura: '180px',
      ordenarPor: (item) => `${item.data ?? ''}${item.hora}`,
      celula: (item) => (
        <div className="flex flex-col gap-0.5">
          <span className="tabular-nums">{formatarData(item.data)}</span>
          <span className="text-micro font-normal uppercase tracking-[0.04em] text-on-surface-muted">às {texto(item.hora)}</span>
        </div>
      ),
    },
    {
      id: 'versao',
      cabecalho: 'Versão',
      largura: '130px',
      ordenarPor: (item) => item.versao,
      celula: (item) => <span className="font-narrow text-[15px] font-bold tabular-nums">{texto(item.versao)}</span>,
    },
    { id: 'status', cabecalho: 'Status', largura: '170px', celula: (item) => <StatusAgendamentoBadge valor={item.status} /> },
    {
      id: 'origem',
      cabecalho: 'Origem',
      largura: '200px',
      secundaria: true,
      celula: (item) => (
        <div className="flex flex-col items-start gap-1">
          <span>{texto(item.origem)}</span>
          {item.batchId ? (
            <Badge tone="primary">
              Lote <span className="font-mono text-[10px]">{item.batchId}</span>
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      id: 'acoes',
      cabecalho: 'Ações',
      largura: podeForcarDisparo ? '200px' : '150px',
      alinhamento: 'right',
      celula: (item) => {
        const regra = podeCancelar(item, porInstalacao.get(item.instalacaoId) ?? null);
        return (
          <div className="flex items-center justify-end gap-1.5">
            {podeForcarDisparo && item.status === 'Programado' ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Forçar disparo imediato do agendamento ${item.id}`}
                    onClick={() => setADisparar(item)}
                  >
                    <Zap className="size-4 text-warning-text" aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Forçar disparo agora (ignora o horário agendado)</TooltipContent>
              </Tooltip>
            ) : null}
            <PermissionButton
              size="sm"
              variant="outline"
              permitido={regra.permitido}
              motivo={regra.motivo}
              onClick={() => setACancelar(item)}
            >
              <Ban aria-hidden="true" />
              Cancelar
            </PermissionButton>
          </div>
        );
      },
    },
  ];

  return (
    <PageBody>
      <PageHeader
        eyebrow="Atualizações"
        titulo="Agendamentos"
        descricao="Tudo o que está programado ou em execução nas instalações do seu escopo."
        acao={
          <Button variant="primary" asChild>
            <Link to="/agendar">
              <CalendarPlus aria-hidden="true" />
              Agendar atualização
            </Link>
          </Button>
        }
      />

      <TabelaPainel
        titulo="Agendamentos"
        contagem={recurso.estado === 'pronto' ? plural(filtrados.length, 'resultado', 'resultados') : undefined}
        filtros={
          <FilterBar>
        <FilterBusca
          id="filtro-busca-agendamento"
          label="Buscar agendamento"
          valor={busca}
          aoMudar={setBusca}
          placeholder="Instalação, versão ou lote"
        />
        <FilterSelect
          id="filtro-status-agendamento"
          label="Status"
          valor={status}
          aoMudar={setStatus}
          rotuloTodos="Todos os status"
          opcoes={[
            { valor: 'Programado', rotulo: 'Programado' },
            { valor: 'Executando', rotulo: 'Executando' },
          ]}
        />
        <FilterLimpar
          ativo={filtrosAtivos > 0}
          quantidade={filtrosAtivos}
          aoLimpar={() => {
            setBusca('');
            setStatus(null);
          }}
        />
          </FilterBar>
        }
      >
          <DataTable
            rotulo="Agendamentos de atualização"
            itens={filtrados}
            colunas={colunas}
            chave={(item) => item.id}
            estado={recurso.estado}
            erro={recurso.erro}
            aoTentarDeNovo={recurso.recarregar}
            vazio={
              filtrosAtivos > 0
                ? {
                    titulo: 'Nenhum agendamento com esses filtros',
                    descricao: 'Ajuste os filtros para ver os demais agendamentos do escopo.',
                  }
                : {
                    titulo: 'Nada agendado',
                    descricao: 'Não há atualizações programadas para as instalações do seu escopo.',
                    acao: (
                      <Button variant="outline" asChild>
                        <Link to="/agendar">Agendar a primeira</Link>
                      </Button>
                    ),
                  }
            }
          />
      </TabelaPainel>

      <Dialog open={Boolean(aCancelar)} onOpenChange={(aberto) => !aberto && setACancelar(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar este agendamento?</DialogTitle>
            <DialogDescription>
              A atualização da versão {texto(aCancelar?.versao)} de {texto(aCancelar?.instalacao)}, marcada para{' '}
              {formatarData(aCancelar?.data)} às {texto(aCancelar?.hora)}, não será executada. A instalação continua na
              versão atual até um novo agendamento.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setACancelar(null)} disabled={cancelamento.executando}>
              Manter agendamento
            </Button>
            <Button
              variant="danger"
              loading={cancelamento.executando}
              onClick={() => {
                const alvo = aCancelar;
                if (alvo) void cancelamento.executar(() => dataSource.cancelarAgendamento(alvo.id));
              }}
            >
              Cancelar agendamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(aDisparar)} onOpenChange={(aberto) => !aberto && setADisparar(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Forçar disparo agora?</DialogTitle>
            <DialogDescription>
              A atualização da versão {texto(aDisparar?.versao)} de {texto(aDisparar?.instalacao)}, marcada para{' '}
              {formatarData(aDisparar?.data)} às {texto(aDisparar?.hora)}, vai disparar em instantes — sem esperar
              esse horário. Ignora o agendamento, não a validação de downgrade nem a capacidade.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setADisparar(null)} disabled={disparoForcado.executando}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={disparoForcado.executando}
              onClick={() => {
                const alvo = aDisparar;
                if (alvo) void disparoForcado.executar(() => dataSource.dispararAgendamentoAgora(alvo.id));
              }}
            >
              <Zap aria-hidden="true" />
              Forçar disparo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageBody>
  );
}
